import useTranslation from "next-translate/useTranslation"
import Link from "next/link"
import { useRouter } from "next/router"
import { useState } from "react"
import { Button, Card, CardBody, CardHeader, Col, Row } from "reactstrap"

import { IProject, IProjectReport, SupportRequestState, UserRole } from "@api/schema"
// import ActionIcon from "@components/common/ActionIcon"
import AuthElement from "@components/common/AuthElement"
import Carousel from "@components/common/Carousel"
import { AvailableProjectContentItems } from "@components/common/contentmanagement/ContentManagementHelper"
import HtmlContent from "@components/common/HtmlContent"
import Icon from "@components/common/Icon"
import SDGElement from "@components/common/SDGElement"
import ShareButton from "@components/common/ShareButton"
import TranslatedHtml from "@components/common/TranslatedHtml"
import SupportRequestScroller from "@components/entityTypes/supportRequest/SupportRequestScroller"
import FeedbackPostPopup from "@components/feedback/FeedbackPostPopup"
// import SinglePostFormCard from "@components/feedback/SinglePostFormCard"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import ReportProjectModal from "@components/marketplace/projects/ReportProjectModal"
import FollowButton from "@components/project/common/FollowButton"
import GoalCard from "@components/project/common/GoalCard"
import ProjectApplyButton from "@components/project/ProjectApplyButton"
import PublicProfileNavigation from "@components/project/PublicProfileNavigation"
import { IFormikActions } from "@redux/helper/actions"
import { IFilteredCollectionState } from "@redux/helper/reducers"
import { useToggleState } from "@services/hooks/useToggleState"
import { NamespaceShortcut, useDynamicTranslation } from "@services/i18n"
import { projectIllustrationPlaceholder } from "@services/projectHelper"
import { Routes, routeWithParams } from "@services/routes"
import { prefixApiUrl } from "@services/util"
import { BASE_URL, MODULE_PARTNER_MARKET_AVAILABLE, MODULE_SDG_AVAILABLE } from "config"

const usedNamespaces: NamespaceShortcut[] = ["partner-market"]

interface IProps {
  addNotification: (...args: any) => void
  reportProject: (project: IProject, report: IProjectReport, actions: IFormikActions) => void
  project: IProject
  roles: UserRole[]
  isMember: boolean
}

/**
 * This component provides all public profile information of a project.
 * It is used by the public profile page.
 *
 * Hint: To add an element of a project like goal explanation, two things needs to be done: adding the content as well as the navigation link.
 *
 */
const PublicProfile: React.FC<IProps> = ({ addNotification, project, reportProject, roles, isMember }: IProps) => {
  const { t } = useTranslation("common")
  const td = useDynamicTranslation()
  const router = useRouter()

  const [supportRequestsLoadingState, setSupportRequestsLoadingState] = useState<IFilteredCollectionState>()
  const { isTrue: reportModalOpen, toggle: toggleReportModal } = useToggleState(false)

  const onReportSubmit = (report: IProjectReport, actions: IFormikActions): void => {
    actions.onSuccess = () => {
      addNotification("message.project.reported")
      toggleReportModal()
    }

    reportProject(project, report, actions)
  }

  /*
  const [chosenContentId, setChosenContentId] = useState("")
  const CommentIcon = (contentId: string) => {
    return <ActionIcon
      className="actionicon"
      onclick={() => {
        setChosenContentId(contentId)
        void router.push("#comment")
      }
      }
      title={t("goto.addComment")}
      icon={"comment-add"}
      id={"comment_" + contentId.replace(".", "_")}
    />
  }
  */
  /*  const [liked, setLiked] = useState(false)
    const toggleLiked = () => setLiked(!liked)*/
  // const likeCount = 32

  const profileFinished =
    !!project.description
    && !!project.challenges
    && !!project.goalExplanation
    && (project.vision || project.visualization)
    && !!project.delimitation



  return <Card className="public-project-profile-card">
    <CardHeader>
      <Row>
        <Col>
          <div className="public-project-profile__picture">
            {!!project.picture || !!project.visualization
              ? <Carousel>
                {!!project.picture && <img
                  src={prefixApiUrl(project.picture.contentUrl)}
                  title={t("project.picture.label")}
                  alt={t("project.picture.label")}
                />}
                {!!project.visualization && <img
                  src={prefixApiUrl(project.visualization.contentUrl)}
                  title={t("project.visualization.label")}
                  alt={t("project.visualization.label")}
                />}
              </Carousel>
              : <img
                className="public-project-profile__picture--placeholder"
                src={projectIllustrationPlaceholder.path}
                title={t("project.picture.placeholder")}
                alt={t("project.picture.placeholder")}
              />
            }
          </div>
        </Col>
        <Col lg={4}>
          <div className="py-5 pr-4 pl-4 pl-lg-0 d-flex flex-column justify-content-between h-100">
            <div>
              {project.categories?.length > 0 && <div className="mb-5">
                <h3>{t("project.categories.label")}</h3>
                {project.categories?.map((category) =>
                  <span key={`category-${category.id}`}>
                    <Link href={`${Routes.Marketplace}?category=${category.id}`}>
                      <a className="btn text-filteritem text-filteritem--small">
                        {category.title}
                      </a>
                    </Link>
                  </span>
                )}
              </div>}
              <div>
                <h3>{t("project.team")}</h3>
                <div
                  className="mt-4 public-project-profile__team d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center"
                >
                  <span className="member-count">{t("project.memberCount", { count: project.memberCount })}</span>
                  {isMember ?
                    <Link href={Routes.MyProjects + "#project-" + project.id.toString()}>
                      <a className="btn primary-btn mt-3">{t("goto.toProject")}</a>
                    </Link>
                    : <ProjectApplyButton project={project} />
                  }
                </div>
              </div>
            </div>
            <div className="mt-4 d-flex align-items-center btn-flex-layout">
              <FollowButton
                project={project}
                showFollowershipCount={true}
                allowWithdrawalOfOnboardingFollowshipInsteadOfModal={false}
              />
              <ShareButton
                url={`${BASE_URL}${router.asPath}`}
                id="profile-share"
              />
              <FeedbackPostPopup
                relatedObject={project}
                className="round-icon-btn"
              />
            </div>
          </div>
        </Col>
      </Row>
    </CardHeader>
    <CardBody>
      <Row>
        <Col className="py-2" md={4}>
          <h3>{t("page.projects.slug.profileHeader")}</h3>
          <div className="mt-4">
            <PublicProfileNavigation
              items={
                [
                  project.description && "description",
                  project.challenges && "challenges",
                  project.goals?.length > 0 && "goals",
                  project.goalExplanation && "goalExplanation",
                  project.implementationLocations?.length > 0 && "implementationLocations",
                  (project.vision || project.visualization) && "vision",
                  project.delimitation && "delimitation"
                ]
              }
            />
          </div>
        </Col>
        <Col className="py-2" lg>
          <p className="font-weight-bold">{project.shortDescription}
            <FeedbackPostPopup contentId={AvailableProjectContentItems.ShortDescription} relatedObject={project} />
          </p>

          {project.description && <div id="description" className="py-3">
            <h3>{t("project.description.label")} <FeedbackPostPopup contentId={AvailableProjectContentItems.Description} relatedObject={project} /></h3>
            <div className="rte-content">
              <HtmlContent content={project.description} />
            </div>
          </div>}

          {project.challenges && <div id="challenges" className="py-3">
            <h3>{t("project.challenges.label")} <FeedbackPostPopup contentId={AvailableProjectContentItems.Challenges} relatedObject={project} /></h3>
            <div className="rte-content">
              <HtmlContent content={project.challenges} />
            </div>
          </div>}

          {project.goals?.length > 0 &&
            <div id="goals" className="py-3">
              <h3>{t("project.goals.label")}<FeedbackPostPopup contentId={AvailableProjectContentItems.Goals} relatedObject={project} /></h3>
              <div className="rte-content">
                {
                  project.goals.map(goal =>
                    <GoalCard goal={goal} key={`goal-${goal.goal}`} />
                  )
                }
              </div>
            </div>
          }

          {project.goalExplanation && <div id="goalExplanation" className="py-3">
            <h3>{t("project.goalExplanation.label")} <FeedbackPostPopup contentId={AvailableProjectContentItems.GoalExplanation} relatedObject={project} /></h3>
            <div className="rte-content">
              <HtmlContent content={project.goalExplanation} />
            </div>
          </div>}

          {project.implementationLocations?.length > 0 && <div id="implementationLocations" className="py-3">
            <h3>{t("project.implementationLocations.label")}<FeedbackPostPopup contentId={AvailableProjectContentItems.ImplementationLocations} relatedObject={project} /></h3>
            <ul>
              {project.implementationLocations.map(place =>
                <li key={`location-${place}`}>{place}</li>
              )}
            </ul>
          </div>
          }

          {(project.vision || project.visualization) && <div id="vision" className="py-3">
            <h3>{t("project.vision.label")}<FeedbackPostPopup contentId={AvailableProjectContentItems.Vision} relatedObject={project} /></h3>
            <div className="d-flex flex-column flex-md-row align-items-start">
              {project.visualization && <a
                className="public-project-profile__visualization"
                href={prefixApiUrl(project.visualization.contentUrl)}
                title={t("project.visualization.label")}
                target="_blank" rel="noreferrer"
              >
                {/* {t("project.visualization.label")} ({project.visualization.dimensions[0]}x{project.visualization.dimensions[1]} px, {humanFileSize(project.visualization.size)})*/}
                <img
                  src={prefixApiUrl(project.visualization.contentUrl)}
                  alt={t("project.visualization.label")}
                  height={150}
                />
                <span className="icon">
                  <Icon name="fullscreen" size={20} />
                </span>
              </a>}
              {project.vision && <div className={`rte-content${project.visualization && " mt-4 mt-md-0 ml-md-4"}`}>
                <HtmlContent content={project.vision} />
              </div>}
            </div>
          </div>}

          {MODULE_SDG_AVAILABLE && project.sdgs && project.sdgs?.length > 0 && <div id="sdgs" className="py-3">
            <h3>{t("project.sdgs.label")}<FeedbackPostPopup contentId={AvailableProjectContentItems.SDGs} relatedObject={project} /></h3>
            {project.sdgs.map((sdg) => <SDGElement
              href={`${Routes.Marketplace}?sdg=${sdg.id}`}
              key={`SDG_${sdg.id}`}
              sdg={sdg}
            />
            )}
          </div>
          }

          {project.delimitation && <div id="delimitation" className="py-3">
            <h3>{t("project.delimitation.label")}<FeedbackPostPopup contentId={AvailableProjectContentItems.Delimitation} relatedObject={project} /></h3>
            <div className="rte-content">
              <HtmlContent content={project.delimitation} />
            </div>
          </div>}
        </Col>
      </Row>

      {!profileFinished && <Row className="mt-5">
        <Col className="text-center">
          <div className="public-project-profile--unfinished">
            <h3>{t("project.profileUnfinished.header")}</h3>
            {isMember ?
              <>
                <p>{t("project.profileUnfinished.bodyForMembers")}</p>

                <Link href={Routes.MyProjects + "#project-" + project.id.toString()}>
                  <a className="btn primary-btn mt-3">{t("goto.toProject")}</a>
                </Link>
              </>
              : <>
                <p>{t("project.profileUnfinished.body")}</p>
                <ProjectApplyButton className="mt-3" project={project} />
              </>
            }
          </div>
        </Col>
      </Row>}
      { /*
        <Row>
          <Col>
            <SinglePostFormCard
              relatedObject={project["@id"]}
              contentId={chosenContentId}
              redirectBack={routeWithParams(Routes.ProjectPage, { slug: project.slug || project.id }) + "?contentId=" + chosenContentId + "#comment"}
            />
          </Col>
        </Row>
            */}

      {MODULE_PARTNER_MARKET_AVAILABLE && <>
        {supportRequestsLoadingState?.totalItems > 0 && <>
          <hr />
          <h3>{td("partner-market", "page.publicProfile.searching")}</h3>
        </>}
        <SupportRequestScroller
          filterCriteria={{
            project: project["@id"],
            // show only published SupportRequests
            "state": SupportRequestState.Public
          }}
          setSupportRequestsLoadingState={setSupportRequestsLoadingState}
          withSupportOfferCreateButton={true}
        />
      </>}
      <Row className="mt-5">
        <Col>
          <div
            className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center"
          >
            <p className="m-0">
              {`${t("project.updatedAt")} `}
              <TranslatedHtml
                content="default.longDate"
                params={{ value: project.updatedAt }}
              />
              {
                // Link to edit the project for the processmanager
              }
              <AuthElement requiredRole={UserRole.ProcessManager} roles={roles}>
                {": "}
                <Link href={routeWithParams(Routes.AdminProjectDetails, { id: project.id })}>
                  <a tabIndex={0} title={t("goto.management.projectManagement")}>
                    {t("goto.management.projectManagement")}
                  </a>
                </Link>
              </AuthElement>
            </p>
            <Button
              onClick={toggleReportModal}
            >
              <Icon name="flag" size={25} />
              {t("goto.reportProject")}
            </Button>
          </div>
        </Col>
      </Row>

      <ReportProjectModal
        modalOpen={reportModalOpen}
        isIdea={false}
        onSubmit={onReportSubmit}
        toggle={toggleReportModal}
      />
    </CardBody>
  </Card >
}

export default withDynamicNamespaces<IProps>(PublicProfile, usedNamespaces)